import {Color as NucleusColor} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {HandleType, Node, Handle, Position, useNodeId, useReactFlow} from 'reactflow';

import {TaskSchemaNodeSection} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNodeData,
  TaskSchemaNodeRowData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {
  ROW_HANDLE_STYLES,
  SPACING,
  FONT_SIZE,
  FONT_WEIGHT,
} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

import {HandlesContext} from '../HandlesContext';

export type TaskSchemaNodeRowRendererProps = TaskSchemaNodeRowData;

function FieldName(props: {fieldName: string; isSelected?: boolean}): JSX.Element {
  return (
    <div
      className={css(ROW_STYLES.fieldName, props.isSelected && ROW_STYLES.selectedField)}
      {...getTestProps({
        component: 'TaskSchemaNodeRowRenderer',
        element: 'fieldName',
      })}
    >
      {props.fieldName}
    </div>
  );
}

function FieldIcon(props: {fieldIcon: BenchlingIconName; isSelected?: boolean}): JSX.Element {
  return (
    <BenchlingIcon
      className={css(ROW_STYLES.fieldIcon, props.isSelected && ROW_STYLES.selectedField)}
      icon={props.fieldIcon}
      dataTestAttrs={{
        component: 'TaskSchemaNodeRowRenderer',
        element: 'fieldIcon',
      }}
    />
  );
}

function shouldHideHandle(rowData: TaskSchemaNodeRowRendererProps, handleType: HandleType): boolean {
  if (rowData.sectionName !== TaskSchemaNodeSection.OUTPUTS) {
    return false;
  }
  return (
    (!rowData.isFlowchartTask && handleType === 'target') ||
    (rowData.isFlowchartTask && handleType === 'source')
  );
}

/**
 * Handles used to draw edges between fields on two nodes.
 */
function RowHandle(props: {
  rowData: TaskSchemaNodeRowRendererProps;
  handleType: HandleType;
  isVisible: boolean;
}): JSX.Element | null {
  const handlesContext = HandlesContext.useContext();
  const rowHandleId = `${props.rowData.id}-${props.handleType}`;
  const isConnected = handlesContext.handlesInUse.filter((handle) => handle === rowHandleId).length > 0;

  if (shouldHideHandle(props.rowData, props.handleType)) {
    return null;
  }

  return (
    <Handle
      className={css(
        ROW_HANDLE_STYLES.rowHandle,
        props.handleType === 'target' ? ROW_HANDLE_STYLES.leftHandle : ROW_HANDLE_STYLES.rightHandle,
        !props.isVisible && !isConnected && ROW_HANDLE_STYLES.hidden
      )}
      type={props.handleType}
      position={props.handleType === 'target' ? Position.Left : Position.Right}
      id={rowHandleId}
    >
      <div className={css(ROW_HANDLE_STYLES.handleDot)} />
    </Handle>
  );
}

// Hook which returns the data for the current node.
// Note that react flow v12 (@xyflow/react) allows us to use the `useNodeData` hook to get the data for the
// current node
function useCurrentNodeData(): TaskSchemaNodeData | null {
  const currentNodeId = useNodeId();
  const reactFlow = useReactFlow();
  if (currentNodeId == null) {
    return null;
  }
  const currentNode: Node<TaskSchemaNodeData> | undefined = reactFlow.getNode(currentNodeId);
  if (currentNode == null) {
    return null;
  }
  return currentNode.data;
}

/**
 * Render a field in the task schema node.
 */
export function TaskSchemaNodeRowRenderer(props: TaskSchemaNodeRowRendererProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const nodeData = useCurrentNodeData();

  return (
    <div
      className={css(
        ROW_STYLES.row,
        nodeData != null && ROW_STYLES.clickableRow,
        !props.isSelected && isHovered && ROW_STYLES.hoveredRow,
        props.isSelected && ROW_STYLES.selectedRow
      )}
      {...getTestProps({
        component: 'TaskSchemaNodeRowRenderer',
        element: 'root',
        key: props.id,
      })}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onClick={() => {
        if (nodeData != null) {
          nodeData.onFieldClick(nodeData.id, props.id);
        }
      }}
    >
      <div className={css(ROW_STYLES.primaryColumn)}>
        {props.fieldIcon && <FieldIcon fieldIcon={props.fieldIcon} isSelected={props.isSelected} />}
        <FieldName fieldName={props.fieldName} isSelected={props.isSelected} />
      </div>
      <RowHandle rowData={props} handleType="target" isVisible={isHovered} />
      <RowHandle rowData={props} handleType="source" isVisible={isHovered} />
    </div>
  );
}

export const ROW_STYLES = StyleSheet.create({
  row: {
    display: 'flex',
    padding: `${SPACING.XSMALL} ${SPACING.MEDIUM_LARGE}`,
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  primaryColumn: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.SMALL,
    flex: 1,
  },
  fieldName: {
    color: Colors.NEAR_BLACK,
    fontSize: FONT_SIZE.SMALL,
    fontWeight: FONT_WEIGHT.NORMAL,
  },
  fieldIcon: {
    color: Colors.GRAY_SEMIDARK,
    fontSize: FONT_SIZE.SMALL,
    fontWeight: FONT_WEIGHT.NORMAL,
  },
  hiddenHandle: {
    visibility: 'hidden',
  },
  hoveredRow: {
    backgroundColor: NucleusColor.GRAY_50,
  },
  selectedRow: {
    backgroundColor: NucleusColor.BLUE_50,
    color: NucleusColor.BLUE_450,
  },
  selectedField: {
    color: NucleusColor.BLUE_450,
  },
});
